/**
 * Cascader每一个子项的逻辑，如无限级联的情况可以递归使用
 */

import React, { useMemo } from 'react';
import { Checkbox, Icon, Bubble } from 'tea-component';
import classNames from 'classnames';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { getShowLabel } from '../../../utils';
import { ICascaderSingleItemProps } from '../../../type';
import SingleText from '../SingleText';
import HighlightText from '@intelligence/components/HighlightText';
import './index.scss';

export default function SingleMenuItem(props: ICascaderSingleItemProps) {
  const {
    formItem,
    formValue,
    isMutiple = false,
    onItemClick,
    onChange,
    isSelected,
    hasHighlight,
    searchKey,
    isGroup,
  } = props;
  const {
    source,
    value: formItemKey = '',
    children = [],
    icon,
    isDisable,
    isNoIcon,
    hideChildrenCount,
  } = formItem;
  const { formatMessage } = useIntlExtend();
  const effectiveIsGroup = !!isGroup && children.length > 0;
  const leafFormValue = Array.isArray(formValue) ? formValue : formValue?.__leafValues || [];

  // 禁用时的提示，业务需要
  const disabledMsg = getShowLabel(formItem, formatMessage, 'disableMsg');

  // 是否为半选状态
  const indeterminate = useMemo(() => {
    if (effectiveIsGroup) {
      const childSelected = formValue[formItemKey] || [];
      return (
        childSelected?.length !== (formItem.selectableChildrenCount || children?.length) && !!childSelected?.length
      );
    }
    return false;
  }, [effectiveIsGroup ? formValue[formItemKey]?.length : leafFormValue?.length, children]);

  // 获取是否选中
  const getChecked = () => {
    if (effectiveIsGroup) {
      // 当formValue包含且child有选择时为选中状态
      const childSelected = formValue[formItemKey] || [];
      // 如果没有子项，则直接选中
      return (
        formItem.isEmpty ||
        (Object.prototype.hasOwnProperty.call(formValue, formItemKey) &&
          !!childSelected?.length)
      );
    }
    // child则判断formValue数组中是否有包含
    return leafFormValue?.includes(formItemKey);
  };

  // change的回调
  const handleChange = (value: boolean, isIndeterminate: boolean) => {
    onChange(formItemKey, effectiveIsGroup, isIndeterminate, value, formItem);
  };

  // 只有为group时响应整行选择
  // TODO 后续产品可能提出点击时选中的逻辑，可以直接调用onChange
  const handleItemClick = () => {
    if (effectiveIsGroup) {
      formItem?.children?.length && onItemClick && onItemClick(formItem);
    } else {
      onChange(
        formItemKey,
        false,
        false,
        !leafFormValue?.includes(formItemKey),
        formItem,
      );
    }
  };

  return (
    <Bubble
      style={{ maxWidth: '400px' }}
      dark
      content={isDisable && disabledMsg}
    >
      {isMutiple ? (
        <Checkbox
          name={formItemKey}
          key={`${source}_${formItemKey}`}
          className={classNames('dbi-cascader-single-item', {
            'is-selected': isSelected,
          })}
          indeterminate={indeterminate}
          disabled={isDisable}
          value={getChecked()}
          onChange={(value: boolean) => {
            handleChange(value, indeterminate);
          }}
        >
          <li
            className={`dbi-cascader-single-item-checkitem`}
            onClick={(e) => {
              e?.stopPropagation();
              e?.preventDefault();
              handleItemClick();
            }}
          >
            <div className="dbi-cascader-single-item-checkitem-flex">
              {!isNoIcon && icon ? (
                <img
                  className="dbi-cascader-common-icon"
                  src={getDataSourceIcon(icon)}
                />
              ) : null}
              <span
                className="dbi-cascader-single-item-checkitem-label"
                title={getShowLabel(formItem, formatMessage, 'label')}
              >
                {hasHighlight && searchKey ? (
                  <HighlightText
                    text={getShowLabel(formItem, formatMessage, 'label')}
                    searchKey={searchKey}
                    color={'#326ff0'}
                  />
                ) : (
                  getShowLabel(formItem, formatMessage, 'label') +
                  (children?.length > 0 && !hideChildrenCount ? `(${children?.length})` : '')
                )}
              </span>
            </div>
            {children?.length > 0 && <Icon type="arrowright" />}
          </li>
        </Checkbox>
      ) : (
        <SingleText formItem={formItem} />
      )}
    </Bubble>
  );
}
